import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  FiCheckSquare,
  FiChevronLeft,
  FiChevronRight,
  FiClock,
  FiEdit2,
  FiEye,
  FiFlag,
  FiGitMerge,
  FiMessageSquare,
  FiPlus,
  FiUserX,
  FiSearch,
  FiX,
} from "react-icons/fi";
import {
  createCrmContact,
  createCrmCompany,
  fetchCrmCompanies,
  fetchCrmCompany,
  fetchCrmConversations,
  fetchCrmLeads,
  fetchCrmTasks,
  mergeCrmCompanies,
  unmergeCrmCompany,
  updateCrmCompany,
  updateCrmContact,
} from "../crmApi";
import CrmMergeHistory from "../components/CrmMergeHistory";
import CrmMergeModal from "../components/CrmMergeModal";
import ZadarmaCallButton from "../telephony/ZadarmaCallButton";
import { useCrmCopy } from "../crmI18n";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";

const emptyForm = {
  name: "",
  legal_name: "",
  tax_id: "",
  website: "",
  email: "",
  phone: "",
  industry: "",
  company_type: "",
  status: "active",
  address: "",
  city: "",
  country: "",
  postal_code: "",
  notes: "",
};

const emptyContactForm = {
  first_name: "",
  last_name: "",
  email: "",
  phone: "",
  company_id: "",
  job_title: "",
  department: "",
  crm_status: "active",
  source: "",
  notes: "",
};

const COMPANIES_PAGE_SIZE = 100;

function formatDateTime(value) {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";
  return new Intl.DateTimeFormat(undefined, {
    month: "short",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  }).format(date);
}

function formatDate(value) {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";
  return new Intl.DateTimeFormat(undefined, {
    year: "numeric",
    month: "short",
    day: "2-digit",
  }).format(date);
}

function getConversationDate(conversation) {
  return conversation?.last_message_at || conversation?.updated_at || conversation?.created_at;
}

function buildFormFromCompany(company) {
  return {
    name: company?.name || "",
    legal_name: company?.legal_name || "",
    tax_id: company?.tax_id || "",
    website: company?.website || "",
    email: company?.email || "",
    phone: company?.phone || "",
    industry: company?.industry || "",
    company_type: company?.company_type || "",
    status: company?.status || "active",
    address: company?.address || "",
    city: company?.city || "",
    country: company?.country || "",
    postal_code: company?.postal_code || "",
    notes: company?.notes || "",
  };
}

function getContactLabel(contact) {
  if (!contact) return "";
  const fullName = contact.full_name || [contact.first_name, contact.last_name].filter(Boolean).join(" ");
  return fullName || contact.email || contact.phone || `Contact ${contact.id || ""}`.trim();
}

function getEntityInitials(label) {
  const parts = String(label || "")
    .trim()
    .split(/\s+/)
    .filter(Boolean);

  if (!parts.length) return "?";
  return parts
    .slice(0, 2)
    .map((part) => part[0])
    .join("")
    .toUpperCase();
}

function getContactStatus(contact) {
  return contact?.crm_status || contact?.customer_status || "active";
}

function isOpportunityLead(lead) {
  return Boolean(lead?.opportunity_started_at);
}

export default function Companies() {
  const copy = useCrmCopy();
  const pageCopy = copy.entities.companies;
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const selectAllRef = useRef(null);
  const openedCompanyQueryRef = useRef("");
  const [rows, setRows] = useState([]);
  const [selectedCompanyIds, setSelectedCompanyIds] = useState([]);
  const [query, setQuery] = useState("");
  const [pageIndex, setPageIndex] = useState(0);
  const [showInactive, setShowInactive] = useState(false);
  const [totalRows, setTotalRows] = useState(0);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [mergeModalOpen, setMergeModalOpen] = useState(false);
  const [mergeSaving, setMergeSaving] = useState(false);
  const [mergeError, setMergeError] = useState("");
  const [unmergeSaving, setUnmergeSaving] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingCompany, setEditingCompany] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [detailModalOpen, setDetailModalOpen] = useState(false);
  const [detailCompany, setDetailCompany] = useState(null);
  const [detailLeads, setDetailLeads] = useState([]);
  const [detailOpportunities, setDetailOpportunities] = useState([]);
  const [detailConversations, setDetailConversations] = useState([]);
  const [detailTasks, setDetailTasks] = useState([]);
  const [detailActiveTab, setDetailActiveTab] = useState("overview");
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailSaving, setDetailSaving] = useState(false);
  const [detailError, setDetailError] = useState("");
  const [contactModalOpen, setContactModalOpen] = useState(false);
  const [contactForm, setContactForm] = useState(emptyContactForm);
  const [contactSaving, setContactSaving] = useState(false);
  const [contactError, setContactError] = useState("");

  const statusOptions = [
    { value: "active", label: copy.common.active },
    { value: "inactive", label: copy.common.inactive },
  ];

  const selectedCompanyIdSet = useMemo(() => new Set(selectedCompanyIds), [selectedCompanyIds]);
  const selectedCompanies = useMemo(
    () => rows.filter((company) => selectedCompanyIdSet.has(company.id)),
    [rows, selectedCompanyIdSet]
  );
  const selectedCompany = selectedCompanies.length === 1 ? selectedCompanies[0] : null;
  const detailContacts = Array.isArray(detailCompany?.related_contacts) ? detailCompany.related_contacts : [];
  const buildCompanyReturnState = useCallback(
    () => {
      const params = new URLSearchParams(location.search);
      if (detailCompany?.id) params.set("company_id", detailCompany.id);
      const search = params.toString();
      return {
        returnTo: {
          pathname: location.pathname,
          search: search ? `?${search}` : "",
          label: detailCompany?.name || copy.sections.companies.title,
        },
      };
    },
    [copy.sections.companies.title, detailCompany?.id, detailCompany?.name, location.pathname, location.search]
  );
  const detailTabs = useMemo(
    () => [
      { key: "overview", label: pageCopy.detail.tabs.overview },
      { key: "leads", label: pageCopy.detail.tabs.leads, count: detailLeads.length },
      { key: "opportunities", label: pageCopy.detail.tabs.opportunities, count: detailOpportunities.length },
      { key: "activity", label: pageCopy.detail.tabs.activity, count: detailConversations.length },
      { key: "tasks", label: pageCopy.detail.tabs.tasks, count: detailTasks.length },
    ],
    [
      detailConversations.length,
      detailLeads.length,
      detailOpportunities.length,
      detailTasks.length,
      pageCopy.detail.tabs.activity,
      pageCopy.detail.tabs.leads,
      pageCopy.detail.tabs.opportunities,
      pageCopy.detail.tabs.overview,
      pageCopy.detail.tabs.tasks,
    ]
  );
  const canMergeSelectedCompanies = selectedCompanies.length >= 2;
  const visibleCompanyIds = useMemo(() => rows.map((company) => company.id).filter(Boolean), [rows]);
  const allVisibleSelected =
    visibleCompanyIds.length > 0 && visibleCompanyIds.every((id) => selectedCompanyIdSet.has(id));
  const partiallySelected = selectedCompanyIds.length > 0 && !allVisibleSelected;
  const pageStart = totalRows ? pageIndex * COMPANIES_PAGE_SIZE + 1 : 0;
  const pageEnd = totalRows ? Math.min((pageIndex + 1) * COMPANIES_PAGE_SIZE, totalRows) : 0;
  const hasPreviousPage = pageIndex > 0;
  const hasNextPage = pageEnd < totalRows;

  const loadCompanies = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const data = await fetchCrmCompanies({
        q: query,
        include_inactive: showInactive ? 1 : 0,
        limit: COMPANIES_PAGE_SIZE,
        offset: pageIndex * COMPANIES_PAGE_SIZE,
      });
      setRows(Array.isArray(data?.rows) ? data.rows : []);
      setTotalRows(Number(data?.total || 0));
    } catch (err) {
      console.error("[CRM] Companies load error:", err);
      setError(err?.response?.data?.error || pageCopy.loadError);
      setRows([]);
      setTotalRows(0);
    } finally {
      setLoading(false);
    }
  }, [pageCopy.loadError, pageIndex, query, showInactive]);

  useEffect(() => {
    loadCompanies();
  }, [loadCompanies]);

  useEffect(() => {
    const visibleIds = new Set(rows.map((company) => company.id));
    setSelectedCompanyIds((current) => {
      const next = current.filter((id) => visibleIds.has(id));
      return next.length === current.length ? current : next;
    });
  }, [rows]);

  useEffect(() => {
    if (selectAllRef.current) {
      selectAllRef.current.indeterminate = partiallySelected;
    }
  }, [partiallySelected]);

  const toggleCompanySelection = (companyId) => {
    setSelectedCompanyIds((current) =>
      current.includes(companyId)
        ? current.filter((id) => id !== companyId)
        : [...current, companyId]
    );
  };

  const toggleVisibleSelection = () => {
    setSelectedCompanyIds((current) => {
      if (allVisibleSelected) {
        return current.filter((id) => !visibleCompanyIds.includes(id));
      }

      return Array.from(new Set([...current, ...visibleCompanyIds]));
    });
  };

  const clearCompanySelection = () => {
    setSelectedCompanyIds([]);
  };

  const openCreate = () => {
    setEditingCompany(null);
    setForm(emptyForm);
    setModalOpen(true);
  };

  const openEdit = async (company) => {
    if (!company?.id) return;
    setError("");
    try {
      const detail = await fetchCrmCompany(company.id);
      setEditingCompany(detail || company);
      setForm(buildFormFromCompany(detail || company));
    } catch (err) {
      console.error("[CRM] Company detail load error:", err);
      setEditingCompany(company);
      setForm(buildFormFromCompany(company));
    }
    setModalOpen(true);
  };

  const refreshCompanyDetail = useCallback(async (companyId) => {
    const [detail, leadsData, opportunitiesData, conversationsData, tasksData] = await Promise.all([
      fetchCrmCompany(companyId),
      fetchCrmLeads({ company_id: companyId, include_inactive: 1, limit: 100 }),
      fetchCrmLeads({ company_id: companyId, opportunity_only: 1, include_inactive: 1, limit: 100 }),
      fetchCrmConversations({ company_id: companyId, limit: 100 }),
      fetchCrmTasks({ company_id: companyId, limit: 100 }),
    ]);
    const leadRows = Array.isArray(leadsData?.rows) ? leadsData.rows : [];
    setDetailCompany(detail);
    setDetailLeads(leadRows.filter((lead) => !isOpportunityLead(lead)));
    setDetailOpportunities(Array.isArray(opportunitiesData?.rows) ? opportunitiesData.rows : []);
    setDetailConversations(Array.isArray(conversationsData?.rows) ? conversationsData.rows : []);
    setDetailTasks(Array.isArray(tasksData?.rows) ? tasksData.rows : []);
    setRows((prev) =>
      prev.map((company) => (Number(company.id) === Number(detail.id) ? { ...company, ...detail } : company))
    );
    return detail;
  }, []);

  const openCompanyDetail = useCallback(async (company) => {
    if (!company?.id) return;
    const companyId = String(company.id);
    const nextParams = new URLSearchParams(searchParams);
    if (nextParams.get("company_id") !== companyId) {
      nextParams.set("company_id", companyId);
      openedCompanyQueryRef.current = companyId;
      setSearchParams(nextParams, { replace: true });
    }
    setDetailModalOpen(true);
    setDetailCompany(company);
    setDetailLeads([]);
    setDetailOpportunities([]);
    setDetailConversations([]);
    setDetailTasks([]);
    setDetailActiveTab("overview");
    setDetailLoading(true);
    setDetailError("");

    try {
      await refreshCompanyDetail(company.id);
    } catch (err) {
      console.error("[CRM] Company detail load error:", err);
      setDetailError(err?.response?.data?.error || pageCopy.detail.loadError);
    } finally {
      setDetailLoading(false);
    }
  }, [pageCopy.detail.loadError, refreshCompanyDetail, searchParams, setSearchParams]);

  const closeCompanyDetail = () => {
    if (detailSaving) return;
    setDetailModalOpen(false);
    setDetailCompany(null);
    setDetailLeads([]);
    setDetailOpportunities([]);
    setDetailConversations([]);
    setDetailTasks([]);
    setDetailActiveTab("overview");
    setDetailError("");
    if (location.state?.returnTo?.pathname && searchParams.has("company_id")) {
      const returnTo = location.state.returnTo;
      navigate(`${returnTo.pathname}${returnTo.search || ""}`, {
        state: returnTo.state || undefined,
      });
      openedCompanyQueryRef.current = "";
      return;
    }
    const nextParams = new URLSearchParams(searchParams);
    nextParams.delete("company_id");
    setSearchParams(nextParams, { replace: true });
    openedCompanyQueryRef.current = "";
  };

  const openDetailEdit = () => {
    if (!detailCompany || detailSaving) return;
    const companyToEdit = detailCompany;
    setDetailModalOpen(false);
    setDetailCompany(null);
    setDetailLeads([]);
    setDetailOpportunities([]);
    setDetailConversations([]);
    setDetailTasks([]);
    setDetailActiveTab("overview");
    setDetailError("");
    openEdit(companyToEdit);
  };

  useEffect(() => {
    const companyId = searchParams.get("company_id");
    if (!companyId || openedCompanyQueryRef.current === companyId) return;
    openedCompanyQueryRef.current = companyId;
    openCompanyDetail({ id: companyId });
  }, [openCompanyDetail, searchParams]);

  const closeModal = () => {
    if (saving) return;
    setModalOpen(false);
    setEditingCompany(null);
    setForm(emptyForm);
  };

  const handleFieldChange = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleContactFieldChange = (field, value) => {
    setContactForm((prev) => ({ ...prev, [field]: value }));
  };

  const openCreateContactForCompany = () => {
    if (!detailCompany?.id || detailSaving) return;
    setDetailError("");
    setContactForm({
      ...emptyContactForm,
      company_id: detailCompany.id,
    });
    setContactError("");
    setContactModalOpen(true);
  };

  const closeContactModal = () => {
    if (contactSaving) return;
    setContactModalOpen(false);
    setContactForm(emptyContactForm);
    setContactError("");
  };

  const handleCreateCompanyContact = async (event) => {
    event.preventDefault();
    if (!detailCompany?.id || contactSaving) return;
    setContactSaving(true);
    setContactError("");

    try {
      await createCrmContact({
        ...contactForm,
        company_id: detailCompany.id,
      });
      setContactModalOpen(false);
      setContactForm(emptyContactForm);
      await refreshCompanyDetail(detailCompany.id);
      await loadCompanies();
    } catch (err) {
      console.error("[CRM] Company contact create error:", err);
      setContactError(err?.response?.data?.error || pageCopy.detail.createContactError);
    } finally {
      setContactSaving(false);
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setSaving(true);
    setError("");

    try {
      if (editingCompany?.id) {
        await updateCrmCompany(editingCompany.id, form);
      } else {
        await createCrmCompany(form);
      }
      setModalOpen(false);
      setEditingCompany(null);
      setForm(emptyForm);
      await loadCompanies();
    } catch (err) {
      console.error("[CRM] Company save error:", err);
      setError(err?.response?.data?.error || pageCopy.saveError);
    } finally {
      setSaving(false);
    }
  };

  const openMergeModal = () => {
    if (!canMergeSelectedCompanies) return;
    setMergeError("");
    setMergeModalOpen(true);
  };

  const closeMergeModal = () => {
    if (mergeSaving) return;
    setMergeModalOpen(false);
    setMergeError("");
  };

  const handleMergeSubmit = async (payload) => {
    setMergeSaving(true);
    setMergeError("");
    setError("");
    try {
      await mergeCrmCompanies(payload);
      setSelectedCompanyIds([]);
      setMergeModalOpen(false);
      await loadCompanies();
    } catch (err) {
      console.error("[CRM] Company merge error:", err);
      setMergeError(err?.response?.data?.error || pageCopy.merge.error);
    } finally {
      setMergeSaving(false);
    }
  };

  const handleUnmergeCompany = async () => {
    if (!editingCompany?.id || unmergeSaving) return;
    setUnmergeSaving(true);
    setError("");
    try {
      const result = await unmergeCrmCompany(editingCompany.id);
      if (result?.record) {
        setEditingCompany(result.record);
        setForm(buildFormFromCompany(result.record));
      }
      await loadCompanies();
    } catch (err) {
      console.error("[CRM] Company unmerge error:", err);
      setError(err?.response?.data?.error || pageCopy.merge.unmergeError);
    } finally {
      setUnmergeSaving(false);
    }
  };

  const handleUnmergeCompanyDetail = async () => {
    if (!detailCompany?.id || unmergeSaving) return;
    setUnmergeSaving(true);
    setDetailError("");
    try {
      await unmergeCrmCompany(detailCompany.id);
      await refreshCompanyDetail(detailCompany.id);
      await loadCompanies();
    } catch (err) {
      console.error("[CRM] Company detail unmerge error:", err);
      setDetailError(err?.response?.data?.error || pageCopy.merge.unmergeError);
    } finally {
      setUnmergeSaving(false);
    }
  };

  const handleUnassignCompanyContact = async (contact) => {
    if (!detailCompany?.id || !contact?.id || detailSaving) return;
    setDetailSaving(true);
    setDetailError("");

    try {
      await updateCrmContact(contact.id, { company_id: null });
      await refreshCompanyDetail(detailCompany.id);
      await loadCompanies();
    } catch (err) {
      console.error("[CRM] Company contact unassign error:", err);
      setDetailError(err?.response?.data?.error || pageCopy.detail.unassignContactError);
    } finally {
      setDetailSaving(false);
    }
  };

  const handleDeactivateCompanyContact = async (contact) => {
    if (!detailCompany?.id || !contact?.id || detailSaving) return;
    setDetailSaving(true);
    setDetailError("");

    try {
      await updateCrmContact(contact.id, { crm_status: "inactive" });
      await refreshCompanyDetail(detailCompany.id);
      await loadCompanies();
    } catch (err) {
      console.error("[CRM] Company contact deactivate error:", err);
      setDetailError(err?.response?.data?.error || pageCopy.detail.deactivateContactError);
    } finally {
      setDetailSaving(false);
    }
  };

  return (
    <main className="crm-page crm-page--compactTable crm-page--companies">
      <header className="crm-pageHeader">
        <div>
          <p className="crm-eyebrow">{copy.common.crm}</p>
          <h1>{copy.sections.companies.title}</h1>
        </div>

        <button type="button" className="crm-ghostButton" onClick={openCreate}>
          <FiPlus aria-hidden="true" />
          <span>{pageCopy.newTitle}</span>
        </button>
      </header>

      <section className="crm-panel">
        <div className="crm-entityToolbar">
          <label className="crm-searchBox">
            <FiSearch aria-hidden="true" />
            <input
              value={query}
              onChange={(event) => {
                setQuery(event.target.value);
                setPageIndex(0);
              }}
              onKeyDown={(event) => {
                if (event.key === "Enter") loadCompanies();
              }}
              placeholder={pageCopy.searchPlaceholder}
            />
          </label>
          <button type="button" className="crm-ghostButton" onClick={loadCompanies} disabled={loading}>
            <FiSearch aria-hidden="true" />
            <span>{loading ? copy.common.loading : copy.common.search}</span>
          </button>
        </div>

        {error ? <div className="crm-stateBanner is-error">{error}</div> : null}

        <section className="crm-leadActionPanel" aria-label={pageCopy.selection.title}>
          <div className="crm-leadActionSummary">
            <strong>{pageCopy.selection.title}</strong>
            <span>
              {selectedCompanyIds.length
                ? pageCopy.selection.selected(selectedCompanyIds.length)
                : pageCopy.selection.noneSelected}
            </span>
          </div>
          <div className="crm-leadActionControls">
            <button
              type="button"
              className="crm-leadActionButton"
              onClick={() => selectedCompany && openCompanyDetail(selectedCompany)}
              disabled={!selectedCompany}
              title={!selectedCompany ? pageCopy.selection.singleOnly : pageCopy.detail.view}
              aria-label={pageCopy.detail.view}
            >
              <FiEye aria-hidden="true" />
              <span>{pageCopy.detail.view}</span>
            </button>
            <button
              type="button"
              className="crm-leadActionButton"
              onClick={() => selectedCompany && openEdit(selectedCompany)}
              disabled={!selectedCompany}
              title={!selectedCompany ? pageCopy.selection.singleOnly : copy.common.edit}
              aria-label={copy.common.edit}
            >
              <FiEdit2 aria-hidden="true" />
              <span>{copy.common.edit}</span>
            </button>
            <button
              type="button"
              className="crm-leadActionButton"
              onClick={openMergeModal}
              disabled={!canMergeSelectedCompanies}
              title={!canMergeSelectedCompanies ? pageCopy.selection.mergeMinimum : pageCopy.merge.title}
              aria-label={pageCopy.merge.title}
            >
              <FiGitMerge aria-hidden="true" />
              <span>{pageCopy.selection.merge}</span>
            </button>
            {selectedCompanyIds.length ? (
              <button type="button" className="crm-leadActionClear" onClick={clearCompanySelection}>
                {pageCopy.selection.clear}
              </button>
            ) : null}
          </div>
        </section>

        <div className="crm-tableShell">
          <table className="crm-table">
            <thead>
              <tr>
                <th className="crm-selectCell">
                  <input
                    ref={selectAllRef}
                    type="checkbox"
                    className="crm-tableCheckbox"
                    checked={allVisibleSelected}
                    onChange={toggleVisibleSelection}
                    disabled={!visibleCompanyIds.length}
                    aria-label={pageCopy.selection.selectVisible}
                  />
                </th>
                <th>{pageCopy.table.name}</th>
                <th>{pageCopy.table.email}</th>
                <th>{pageCopy.table.phone}</th>
                <th>{pageCopy.table.industry}</th>
                <th>{pageCopy.table.status}</th>
                <th>{pageCopy.table.contacts}</th>
              </tr>
            </thead>
            <tbody>
              {rows.length ? (
                rows.map((company) => {
                  const isInactiveCompany = company.status === "inactive";
                  const isSelectedCompany = selectedCompanyIdSet.has(company.id);
                  const rowClassName = [
                    isInactiveCompany ? "crm-tableRow--inactive" : "",
                    isSelectedCompany ? "crm-tableRow--selected" : "",
                  ].filter(Boolean).join(" ");

                  return (
                    <tr key={company.id} className={rowClassName || undefined}>
                      <td className="crm-selectCell">
                        <input
                          type="checkbox"
                          className="crm-tableCheckbox"
                          checked={isSelectedCompany}
                          onChange={() => toggleCompanySelection(company.id)}
                          aria-label={pageCopy.selection.selectCompany(company.name)}
                        />
                      </td>
                      <td>
                        <button
                          type="button"
                          className="crm-tablePrimaryButton"
                          onClick={() => openCompanyDetail(company)}
                        >
                          {company.name}
                        </button>
                        {company.legal_name ? (
                          <span className="crm-tableSecondary">{company.legal_name}</span>
                        ) : null}
                      </td>
                      <td>{company.email || "-"}</td>
                      <td>
                        {company.phone ? (
                          <div className="crm-phoneCallCell">
                            <span>{company.phone}</span>
                            <ZadarmaCallButton
                              phones={company.phone}
                              label={company.name || pageCopy.detail.untitled}
                              entityType="company"
                              entityId={company.id}
                              showWhenUnavailable
                              className="crm-tableCallButton"
                            />
                          </div>
                        ) : (
                          "-"
                        )}
                      </td>
                      <td>{company.industry || "-"}</td>
                      <td>
                        <span className={`crm-statusPill crm-statusPill--${company.status || "active"}`}>
                          {company.status === "inactive" ? copy.common.inactive : copy.common.active}
                        </span>
                      </td>
                      <td>
                        <button
                          type="button"
                          className="crm-countLinkButton"
                          onClick={() => openCompanyDetail(company)}
                          aria-label={pageCopy.detail.viewContacts(company.name)}
                        >
                          {company.contact_count || 0}
                        </button>
                      </td>
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td colSpan="7">
                    <div className="crm-tableEmpty">{loading ? copy.common.loading : pageCopy.empty}</div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        <div className="crm-paginationBar">
          <span>{pageCopy.pagination.summary(pageStart, pageEnd, totalRows)}</span>
          <div className="crm-paginationRight">
            <div className="crm-paginationControls">
              <button
                type="button"
                className="crm-ghostButton"
                onClick={() => setPageIndex((prev) => Math.max(prev - 1, 0))}
                disabled={loading || !hasPreviousPage}
              >
                <FiChevronLeft aria-hidden="true" />
                <span>{pageCopy.pagination.previous}</span>
              </button>
              <span className="crm-paginationPage">{pageCopy.pagination.page(pageIndex + 1)}</span>
              <button
                type="button"
                className="crm-ghostButton"
                onClick={() => setPageIndex((prev) => prev + 1)}
                disabled={loading || !hasNextPage}
              >
                <span>{pageCopy.pagination.next}</span>
                <FiChevronRight aria-hidden="true" />
              </button>
            </div>
            <button
              type="button"
              className={`crm-ghostButton crm-inactiveToggle${showInactive ? " is-active" : ""}`}
              onClick={() => {
                setShowInactive((prev) => !prev);
                setPageIndex(0);
              }}
              disabled={loading}
            >
              {showInactive ? pageCopy.pagination.hideInactive : pageCopy.pagination.showInactive}
            </button>
          </div>
        </div>
      </section>

      <CrmMergeModal
        open={mergeModalOpen}
        copy={pageCopy.merge}
        items={selectedCompanies}
        getItemLabel={(company) => company?.name || `Company ${company?.id || ""}`}
        saving={mergeSaving}
        error={mergeError}
        onClose={closeMergeModal}
        onSubmit={handleMergeSubmit}
      />

      {detailModalOpen ? (
        <div className="crm-modalBackdrop" role="presentation">
          <section
            className="crm-modal crm-leadDetailModal crm-companyDetailModal"
            role="dialog"
            aria-modal="true"
            aria-label={pageCopy.detail.title}
          >
            <div className="crm-modalHeader">
              <h2>{pageCopy.detail.title}</h2>
              <div className="crm-modalHeaderActions">
                <button
                  type="button"
                  className="crm-ghostButton"
                  onClick={openDetailEdit}
                  disabled={!detailCompany || detailLoading || detailSaving}
                >
                  <FiEdit2 aria-hidden="true" />
                  <span>{copy.common.edit}</span>
                </button>
                <button
                  type="button"
                  className="crm-iconButton"
                  onClick={closeCompanyDetail}
                  aria-label={copy.common.cancel}
                >
                  <FiX aria-hidden="true" />
                </button>
              </div>
            </div>

            {detailError ? <div className="crm-stateBanner is-error">{detailError}</div> : null}

            {detailCompany ? (
              <>
                <div className="crm-leadDetailHero">
                  <div>
                    <span className={`crm-statusPill crm-statusPill--${detailCompany.status || "active"}`}>
                      {detailCompany.status === "inactive" ? copy.common.inactive : copy.common.active}
                    </span>
                    <h3>{detailCompany.name || pageCopy.detail.untitled}</h3>
                    <p>{detailCompany.legal_name || detailCompany.tax_id || detailCompany.industry || "-"}</p>
                  </div>
                  <div className="crm-leadDetailOwner">
                    <span>{pageCopy.detail.contacts}</span>
                    <strong>{detailContacts.length}</strong>
                  </div>
                </div>

                <div className="crm-leadDetailStats">
                  <div>
                    <span>{pageCopy.fields.email}</span>
                    <strong>{detailCompany.email || "-"}</strong>
                  </div>
                  <div>
                    <span>{pageCopy.fields.phone}</span>
                    <strong>
                      {detailCompany.phone ? (
                        <span className="crm-phoneCallCell">
                          <span>{detailCompany.phone}</span>
                          <ZadarmaCallButton
                            phones={detailCompany.phone}
                            label={detailCompany.name || pageCopy.detail.untitled}
                            entityType="company"
                            entityId={detailCompany.id}
                            showWhenUnavailable
                            className="crm-tableCallButton"
                          />
                        </span>
                      ) : (
                        "-"
                      )}
                    </strong>
                  </div>
                  <div>
                    <span>{pageCopy.fields.industry}</span>
                    <strong>{detailCompany.industry || "-"}</strong>
                  </div>
                  <div>
                    <span>{pageCopy.fields.city}</span>
                    <strong>{detailCompany.city || detailCompany.country || "-"}</strong>
                  </div>
                </div>

                <div className="crm-tabs" role="tablist" aria-label={pageCopy.detail.tabsLabel}>
                  {detailTabs.map((tab) => (
                    <button
                      key={tab.key}
                      type="button"
                      role="tab"
                      aria-selected={detailActiveTab === tab.key}
                      className={detailActiveTab === tab.key ? "crm-tab crm-tab--active" : "crm-tab"}
                      onClick={() => setDetailActiveTab(tab.key)}
                    >
                      <span>{tab.label}</span>
                      {typeof tab.count === "number" ? <small>{tab.count}</small> : null}
                    </button>
                  ))}
                </div>

                <div className="crm-tabPanel crm-companyDetailTabPanel">
                  {detailActiveTab === "overview" ? (
                    <CompanyOverviewTab
                      company={detailCompany}
                      contacts={detailContacts}
                      copy={copy}
                      pageCopy={pageCopy}
                      loading={detailLoading}
                      saving={detailSaving}
                      onOpenContact={(contact) =>
                        navigate(`/crm/contacts/${contact.id}`, {
                          state: buildCompanyReturnState(),
                        })
                      }
                      onAddContact={openCreateContactForCompany}
                      onUnassignContact={handleUnassignCompanyContact}
                      onDeactivateContact={handleDeactivateCompanyContact}
                    />
                  ) : null}
                  {detailActiveTab === "leads" ? (
                    <CompanyLeadListTab
                      leads={detailLeads}
                      copy={copy}
                      pageCopy={pageCopy}
                      loading={detailLoading}
                      emptyLabel={pageCopy.detail.emptyLeads}
                      fallbackLabel="Lead"
                      onOpenLead={(lead) =>
                        navigate(`/crm/leads?lead_id=${lead.id}`, {
                          state: buildCompanyReturnState(),
                        })
                      }
                      onOpenContact={(contact) =>
                        navigate(`/crm/contacts/${contact.id}`, {
                          state: buildCompanyReturnState(),
                        })
                      }
                    />
                  ) : null}
                  {detailActiveTab === "opportunities" ? (
                    <CompanyLeadListTab
                      leads={detailOpportunities}
                      copy={copy}
                      pageCopy={pageCopy}
                      loading={detailLoading}
                      emptyLabel={pageCopy.detail.emptyOpportunities}
                      fallbackLabel={copy.entities.leads.opportunities?.tableTitle || "Opportunity"}
                      onOpenLead={(lead) =>
                        navigate(`/crm/opportunities?lead_id=${lead.id}`, {
                          state: buildCompanyReturnState(),
                        })
                      }
                      onOpenContact={(contact) =>
                        navigate(`/crm/contacts/${contact.id}`, {
                          state: buildCompanyReturnState(),
                        })
                      }
                    />
                  ) : null}
                  {detailActiveTab === "activity" ? (
                    <CompanyActivityTab
                      conversations={detailConversations}
                      copy={copy}
                      pageCopy={pageCopy}
                      loading={detailLoading}
                    />
                  ) : null}
                  {detailActiveTab === "tasks" ? (
                    <CompanyTasksTab
                      tasks={detailTasks}
                      copy={copy}
                      pageCopy={pageCopy}
                      loading={detailLoading}
                      onOpenLead={(lead) =>
                        navigate(`/crm/${isOpportunityLead(lead) ? "opportunities" : "leads"}?lead_id=${lead.id}`, {
                          state: buildCompanyReturnState(),
                        })
                      }
                      onOpenContact={(contact) =>
                        navigate(`/crm/contacts/${contact.id}`, {
                          state: buildCompanyReturnState(),
                        })
                      }
                    />
                  ) : null}
                </div>

                <div className="crm-modalActions">
                  {detailCompany?.merge_info?.can_unmerge ? (
                    <button
                      type="button"
                      className="crm-ghostButton"
                      onClick={handleUnmergeCompanyDetail}
                      disabled={detailSaving || unmergeSaving}
                    >
                      <FiGitMerge aria-hidden="true" />
                      <span>{unmergeSaving ? pageCopy.merge.saving : pageCopy.merge.unmerge}</span>
                    </button>
                  ) : null}
                  <button type="button" className="crm-primaryButton" onClick={closeCompanyDetail}>
                    {copy.common.cancel}
                  </button>
                </div>
              </>
            ) : (
              <div className="crm-tableEmpty">{copy.common.loading}</div>
            )}
          </section>
        </div>
      ) : null}

      {modalOpen ? (
        <div className="crm-modalBackdrop" role="presentation">
          <form className="crm-modal" onSubmit={handleSubmit}>
            <div className="crm-modalHeader">
              <h2>{editingCompany ? pageCopy.editTitle : pageCopy.newTitle}</h2>
              <button type="button" className="crm-iconButton" onClick={closeModal} aria-label={copy.common.cancel}>
                <FiX aria-hidden="true" />
              </button>
            </div>

            {contactError ? <div className="crm-stateBanner is-error">{contactError}</div> : null}

            <div className="crm-formGrid">
              <Field
                label={pageCopy.fields.name}
                value={form.name}
                required
                onChange={(value) => handleFieldChange("name", value)}
              />
              <Field
                label={pageCopy.fields.legal_name}
                value={form.legal_name}
                onChange={(value) => handleFieldChange("legal_name", value)}
              />
              <Field
                label={pageCopy.fields.tax_id}
                value={form.tax_id}
                onChange={(value) => handleFieldChange("tax_id", value)}
              />
              <Field
                label={pageCopy.fields.website}
                value={form.website}
                onChange={(value) => handleFieldChange("website", value)}
              />
              <Field
                label={pageCopy.fields.email}
                value={form.email}
                type="email"
                onChange={(value) => handleFieldChange("email", value)}
              />
              <Field
                label={pageCopy.fields.phone}
                value={form.phone}
                onChange={(value) => handleFieldChange("phone", value)}
              />
              <Field
                label={pageCopy.fields.industry}
                value={form.industry}
                onChange={(value) => handleFieldChange("industry", value)}
              />
              <Field
                label={pageCopy.fields.company_type}
                value={form.company_type}
                onChange={(value) => handleFieldChange("company_type", value)}
              />
              <SelectField
                label={pageCopy.fields.status}
                value={form.status}
                onChange={(value) => handleFieldChange("status", value)}
                options={statusOptions}
              />
              <Field
                label={pageCopy.fields.address}
                value={form.address}
                onChange={(value) => handleFieldChange("address", value)}
              />
              <Field
                label={pageCopy.fields.city}
                value={form.city}
                onChange={(value) => handleFieldChange("city", value)}
              />
              <Field
                label={pageCopy.fields.country}
                value={form.country}
                onChange={(value) => handleFieldChange("country", value)}
              />
              <Field
                label={pageCopy.fields.postal_code}
                value={form.postal_code}
                onChange={(value) => handleFieldChange("postal_code", value)}
              />
              <Field
                label={pageCopy.fields.notes}
                value={form.notes}
                textarea
                onChange={(value) => handleFieldChange("notes", value)}
              />
            </div>

            {editingCompany ? (
              <CrmMergeHistory copy={pageCopy.merge} mergeInfo={editingCompany.merge_info} />
            ) : null}

            <div className="crm-modalActions">
              {editingCompany?.merge_info?.can_unmerge ? (
                <button
                  type="button"
                  className="crm-ghostButton"
                  onClick={handleUnmergeCompany}
                  disabled={saving || unmergeSaving}
                >
                  <FiGitMerge aria-hidden="true" />
                  <span>{unmergeSaving ? pageCopy.merge.saving : pageCopy.merge.unmerge}</span>
                </button>
              ) : null}
              <button type="button" className="crm-ghostButton" onClick={closeModal} disabled={saving}>
                {copy.common.cancel}
              </button>
              <button type="submit" className="crm-primaryButton" disabled={saving}>
                {saving ? copy.common.saving : copy.common.save}
              </button>
            </div>
          </form>
        </div>
      ) : null}

      {contactModalOpen ? (
        <div className="crm-modalBackdrop" role="presentation">
          <form className="crm-modal" onSubmit={handleCreateCompanyContact}>
            <div className="crm-modalHeader">
              <h2>{pageCopy.detail.addContact}</h2>
              <button
                type="button"
                className="crm-iconButton"
                onClick={closeContactModal}
                aria-label={copy.common.cancel}
              >
                <FiX aria-hidden="true" />
              </button>
            </div>

            <div className="crm-formGrid">
              <Field
                label={copy.entities.contacts.fields.first_name}
                value={contactForm.first_name}
                onChange={(value) => handleContactFieldChange("first_name", value)}
              />
              <Field
                label={copy.entities.contacts.fields.last_name}
                value={contactForm.last_name}
                onChange={(value) => handleContactFieldChange("last_name", value)}
              />
              <Field
                label={copy.entities.contacts.fields.email}
                value={contactForm.email}
                type="email"
                onChange={(value) => handleContactFieldChange("email", value)}
              />
              <Field
                label={copy.entities.contacts.fields.phone}
                value={contactForm.phone}
                onChange={(value) => handleContactFieldChange("phone", value)}
              />
              <Field
                label={copy.entities.contacts.fields.job_title}
                value={contactForm.job_title}
                onChange={(value) => handleContactFieldChange("job_title", value)}
              />
              <Field
                label={copy.entities.contacts.fields.department}
                value={contactForm.department}
                onChange={(value) => handleContactFieldChange("department", value)}
              />
              <SelectField
                label={copy.entities.contacts.fields.crm_status}
                value={contactForm.crm_status}
                onChange={(value) => handleContactFieldChange("crm_status", value)}
                options={statusOptions}
              />
              <Field
                label={copy.entities.contacts.fields.source}
                value={contactForm.source}
                onChange={(value) => handleContactFieldChange("source", value)}
              />
              <Field
                label={copy.entities.contacts.fields.notes}
                value={contactForm.notes}
                textarea
                onChange={(value) => handleContactFieldChange("notes", value)}
              />
            </div>

            <div className="crm-modalActions">
              <button type="button" className="crm-ghostButton" onClick={closeContactModal} disabled={contactSaving}>
                {copy.common.cancel}
              </button>
              <button type="submit" className="crm-primaryButton" disabled={contactSaving}>
                {contactSaving ? copy.common.saving : copy.common.save}
              </button>
            </div>
          </form>
        </div>
      ) : null}
    </main>
  );
}

function CompanyOverviewTab({
  company,
  contacts,
  copy,
  pageCopy,
  loading,
  saving,
  onOpenContact,
  onAddContact,
  onUnassignContact,
  onDeactivateContact,
}) {
  return (
    <>
      <div className="crm-leadDetailGrid">
        <section className="crm-leadDetailPanel crm-companyDetailContactsPanel">
          <div className="crm-leadDetailSectionHeader">
            <h3>{pageCopy.detail.contacts}</h3>
            <div className="crm-companyContactHeaderActions">
              <span>{contacts.length}</span>
              <button type="button" className="crm-ghostButton" onClick={onAddContact} disabled={saving}>
                <FiPlus aria-hidden="true" />
                <span>{pageCopy.detail.addContact}</span>
              </button>
            </div>
          </div>

          <div className="crm-leadRelationList crm-companyContactList">
            {contacts.length ? (
              contacts.map((contact) => {
                const contactLabel = getContactLabel(contact);
                const contactStatus = getContactStatus(contact);
                const isInactiveContact = contactStatus === "inactive";

                return (
                  <article key={contact.id} className="crm-leadRelationCard crm-companyContactCard">
                    <div className="crm-leadRelationAvatar" aria-hidden="true">
                      {getEntityInitials(contactLabel)}
                    </div>
                    <div className="crm-leadRelationMain">
                      <div className="crm-leadRelationTitle">
                        <button
                          type="button"
                          className="crm-inlineLinkButton"
                          onClick={() => onOpenContact(contact)}
                        >
                          {contactLabel}
                        </button>
                        <span className={`crm-statusPill crm-statusPill--${isInactiveContact ? "inactive" : "active"}`}>
                          {isInactiveContact ? copy.common.inactive : copy.common.active}
                        </span>
                      </div>
                      <div className="crm-leadRelationMeta">
                        {contact.email ? <span>{contact.email}</span> : null}
                        {contact.phone ? <span>{contact.phone}</span> : null}
                        {contact.job_title ? <span>{contact.job_title}</span> : null}
                        {contact.department ? <span>{contact.department}</span> : null}
                      </div>
                    </div>
                    <div className="crm-companyContactActions">
                      <button
                        type="button"
                        className="crm-dangerTextButton"
                        onClick={() => onUnassignContact(contact)}
                        disabled={saving}
                      >
                        <FiX aria-hidden="true" />
                        <span>{pageCopy.detail.unassignContact}</span>
                      </button>
                      <button
                        type="button"
                        className="crm-dangerTextButton"
                        onClick={() => onDeactivateContact(contact)}
                        disabled={saving || isInactiveContact}
                      >
                        <FiUserX aria-hidden="true" />
                        <span>{pageCopy.detail.deactivateContact}</span>
                      </button>
                    </div>
                  </article>
                );
              })
            ) : (
              <div className="crm-tableEmpty">
                {loading ? copy.common.loading : pageCopy.detail.emptyContacts}
              </div>
            )}
          </div>
        </section>

        <section className="crm-leadDetailPanel">
          <div className="crm-leadDetailSectionHeader">
            <h3>{pageCopy.fields.notes}</h3>
          </div>
          <p className="crm-leadDetailNotes">{company?.notes || pageCopy.detail.noNotes}</p>
        </section>
      </div>

      <CrmMergeHistory copy={pageCopy.merge} mergeInfo={company?.merge_info} />
    </>
  );
}

function CompanyLeadListTab({
  leads,
  copy,
  pageCopy,
  loading,
  emptyLabel,
  fallbackLabel,
  onOpenLead,
  onOpenContact,
}) {
  if (loading) {
    return <div className="crm-emptyList">{copy.common.loading}</div>;
  }

  if (!leads.length) {
    return <div className="crm-emptyList">{emptyLabel}</div>;
  }

  return (
    <div className="crm-detailList">
      {leads.map((lead) => {
        const leadMeta = [getContactLabel(lead.contact), lead.source].filter(Boolean).join(" · ");
        const stageKey = lead.stage || "new";

        return (
          <article key={lead.id} className="crm-detailListRow">
            <div className="crm-detailListIcon">
              <FiFlag aria-hidden="true" />
            </div>
            <div className="crm-detailListMain">
              <button type="button" className="crm-detailListTitleButton" onClick={() => onOpenLead(lead)}>
                {lead.title || `${fallbackLabel} ${lead.id}`}
              </button>
              {lead.contact?.id ? (
                <button type="button" className="crm-detailListSubButton" onClick={() => onOpenContact(lead.contact)}>
                  {leadMeta || copy.common.noContact}
                </button>
              ) : (
                <span>{leadMeta || copy.common.noContact}</span>
              )}
            </div>
            <div className="crm-detailListMeta">
              <span>
                <FiClock aria-hidden="true" />
                {lead.expected_close_date ? formatDate(lead.expected_close_date) : pageCopy.detail.noDueDate}
              </span>
              <span className={`crm-statusPill crm-statusPill--${stageKey}`}>
                {copy.leadStages?.[stageKey] || stageKey || "-"}
              </span>
            </div>
          </article>
        );
      })}
    </div>
  );
}

function CompanyActivityTab({ conversations, copy, pageCopy, loading }) {
  if (loading) {
    return <div className="crm-emptyList">{copy.common.loading}</div>;
  }

  if (!conversations.length) {
    return <div className="crm-emptyList">{pageCopy.detail.emptyActivity}</div>;
  }

  return (
    <div className="crm-detailList">
      {conversations.map((conversation) => (
        <article key={conversation.id} className="crm-detailListRow">
          <div className="crm-detailListIcon">
            <FiMessageSquare aria-hidden="true" />
          </div>
          <div className="crm-detailListMain">
            <strong>{conversation.subject}</strong>
            <span>
              {copy.conversationChannels?.[conversation.channel] || conversation.channel}
              {conversation.provider ? ` · ${conversation.provider}` : ""}
            </span>
          </div>
          <div className="crm-detailListMeta">
            <span>{formatDateTime(getConversationDate(conversation))}</span>
            <span className={`crm-statusPill crm-statusPill--${conversation.status || "open"}`}>
              {copy.entities.activity.status?.[conversation.status] || conversation.status}
            </span>
          </div>
        </article>
      ))}
    </div>
  );
}

function CompanyTaskContextLink({ task, copy, onOpenLead, onOpenContact }) {
  if (task?.lead?.id) {
    return (
      <button type="button" className="crm-detailListSubButton" onClick={() => onOpenLead(task.lead)}>
        {task.lead.title || `Lead ${task.lead.id}`}
      </button>
    );
  }

  if (task?.contact?.id) {
    return (
      <button type="button" className="crm-detailListSubButton" onClick={() => onOpenContact(task.contact)}>
        {getContactLabel(task.contact)}
      </button>
    );
  }

  if (task?.conversation?.subject) return task.conversation.subject;
  return copy.common.noConversation;
}

function CompanyTasksTab({ tasks, copy, pageCopy, loading, onOpenLead, onOpenContact }) {
  if (loading) {
    return <div className="crm-emptyList">{copy.common.loading}</div>;
  }

  if (!tasks.length) {
    return <div className="crm-emptyList">{pageCopy.detail.emptyTasks}</div>;
  }

  return (
    <div className="crm-detailList">
      {tasks.map((task) => (
        <article key={task.id} className="crm-detailListRow">
          <div className="crm-detailListIcon">
            <FiCheckSquare aria-hidden="true" />
          </div>
          <div className="crm-detailListMain">
            <strong>{task.title}</strong>
            <span>
              {copy.taskTypes?.[task.task_type] || task.task_type}
              {" · "}
              <CompanyTaskContextLink
                task={task}
                copy={copy}
                onOpenLead={onOpenLead}
                onOpenContact={onOpenContact}
              />
            </span>
          </div>
          <div className="crm-detailListMeta">
            <span>
              <FiClock aria-hidden="true" />
              {task.due_at ? formatDateTime(task.due_at) : pageCopy.detail.noDueDate}
            </span>
            <span className={`crm-statusPill crm-statusPill--${task.status || "open"}`}>
              {copy.taskStatuses?.[task.status] || task.status}
            </span>
          </div>
        </article>
      ))}
    </div>
  );
}

function Field({ label, value, onChange, type = "text", textarea = false, required = false }) {
  return (
    <label className={`crm-field ${textarea ? "crm-field--wide" : ""}`}>
      <span>{label}</span>
      {textarea ? (
        <textarea
          value={value}
          onChange={(event) => onChange(event.target.value)}
          rows={4}
          required={required}
        />
      ) : (
        <input
          type={type}
          value={value}
          onChange={(event) => onChange(event.target.value)}
          required={required}
        />
      )}
    </label>
  );
}

function SelectField({ label, value, onChange, options }) {
  return (
    <label className="crm-field">
      <span>{label}</span>
      <select value={value} onChange={(event) => onChange(event.target.value)}>
        {options.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
    </label>
  );
}
